import React, { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import {
  Grid,
  GridRow,
  GridColumn,
  Gutter,
  Tabs,
  TabsItem,
  Visible,
  Button,
  useApi,
  Suspense,
  isUuidUtil,
} from '@axiom/ui';
import { Navigate, useParams, useSearchParams } from 'react-router';
import type {
  AfcSearchFilters,
  TalentSearchDisplayFilters,
  AfcCandidateSearch,
} from '@axiom/validation';
import { TALENT_SEARCH_PAGE_SIZE } from '@axiom/const';

import { isEnvoyUser } from '../../utils/roles-util';
import { localStorageUtil } from '../../utils/local-storage-util';
import { WindowUtil } from '../../utils/window-util';
import {
  WHITELISTED_COUNTRY_CODES,
  TalentSearchFilterUtil,
} from '../../utils/talent-search-filter-util';
import { TalentSearchFiltersForm } from '../TalentSearchFiltersForm/TalentSearchFiltersForm';
import { CandidateApi } from '../../api/candidate-api';
import { UserApi } from '../../api/user-api';
import { TalentSearchOptions } from '../TalentSearchOptions/TalentSearchOptions';
import { ExternalSearchReachOutModal } from '../ExternalSearchReachOutModal/ExternalSearchReachOutModal';
import { ContactAxiomModal } from '../ContactAxiomModal/ContactAxiomModal';
import type { TalentSearchActiveQueryFilters } from '../../schema/talent-search-active-query-filters';

import { TalentSearchFindTalent } from './TalentSearchFindTalent';
import { TalentSearchFavorited } from './TalentSearchFavorited';
import { TalentSearchFilterSidedrawer } from './TalentSearchFilterSidedrawer';

export type TalentSearchProps = {
  name?: string;
};

export const TalentSearch = ({ name = 'TALENT_SEARCH' }: TalentSearchProps) => {
  const { pageNumber } = useParams();
  const query = WindowUtil.getUrlQuery();
  const activeFilters = { ...query } as TalentSearchActiveQueryFilters;
  const [{ data: user }] = useApi(UserApi.readUser());
  const [{ data: allFilters }] = useApi(CandidateApi.readCandidateFilters());
  const requestedPage = pageNumber ? +pageNumber : 1;
  const [searchResponse] = useApi(
    CandidateApi.readCandidateSearchResults({
      page: requestedPage,
      search: (activeFilters.search as string) || '',
      sort: (activeFilters.sort as string) || '',
      filters: activeFilters,
      pageSize: TALENT_SEARCH_PAGE_SIZE,
    })
  );
  const { pageCount } = searchResponse.meta;
  const isPageOutOfRange = pageCount > 0 && requestedPage > pageCount;
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view');

  const [filters, setFilters] = useState({});
  const [
    showHubspotExternalReachOutModal,
    setShowHubspotExternalReachOutModal,
  ] = useState(false);
  const [showMobileFilterModal, setShowMobileFilterModal] = useState(false);
  const [inquiredCandidate, setInquiredCandidate] =
    useState<AfcCandidateSearch | null>(null);
  const [displayFilters, setDisplayFilters] =
    useState<TalentSearchDisplayFilters>({} as TalentSearchDisplayFilters);
  const [isPricingShown, setIsPricingShown] = useState(
    !!localStorageUtil.showPricing().get()
  );

  const [{ data: savedCandidates }] = useApi(
    CandidateApi.readFavoriteCandidates()
  );
  useEffect(() => {
    setFilters({
      ...allFilters,
      addressCountryCode: allFilters.addressCountryCode.filter(acc =>
        WHITELISTED_COUNTRY_CODES.has(acc.id)
      ),
    });
  }, [allFilters]);

  // NOTE: industries were converted from UUIDs to enums
  // This is to handle an edge case where a user bookmarked a search with
  // a bad format
  useEffect(() => {
    let queryChanged = false;

    if (
      query.industries &&
      typeof query.industries !== 'string' &&
      Array.isArray(query.industries) &&
      query.industries.some(opt => isUuidUtil(opt as string))
    ) {
      delete query.industries;
      queryChanged = true;
    }

    if (query.weeklyAvailabilityLabel) {
      delete query.weeklyAvailabilityLabel;
      queryChanged = true;
    }

    if (queryChanged) {
      WindowUtil.setUrlQuery(query);
    }
  }, []);

  useEffect(() => {
    if (Object.keys(filters).length > 0) {
      const organizedData = TalentSearchFilterUtil.organizeData({
        allFilters: filters as AfcSearchFilters,
        defaultSort: 'relevance',
        activeFilters,
      });
      setDisplayFilters(organizedData);
    }
  }, [filters, JSON.stringify(activeFilters)]);

  const isHomeTabSelected = view !== 'saved';

  const handleCandidateRefresh = async () => {
    await Promise.all([
      CandidateApi.refreshCandidateSearchResults(),
      CandidateApi.refreshFavoriteCandidates(),
    ]);
  };

  if (isPageOutOfRange) {
    return <Navigate to="/404" replace />;
  }

  return (
    <Suspense>
      <Grid name={name}>
        <GridRow>
          <GridColumn mobileWidth={12} tabletWidth={4} largeScreenWidth={4}>
            {isEmpty(displayFilters) ? null : (
              <TalentSearchFiltersForm
                disabled={!isHomeTabSelected}
                displayFilters={displayFilters}
              />
            )}
          </GridColumn>
          <GridColumn mobileWidth={12} tabletWidth={8} largeScreenWidth={8}>
            <Tabs
              name="TALENT_SEARCH_TABS"
              startTab={isHomeTabSelected ? 'HOME_TAB' : 'FAVORITES_TAB'}
              appendToRight={
                isEnvoyUser(user) ? (
                  <TalentSearchOptions
                    updateState={async () => {
                      setIsPricingShown(prev => !prev);
                      await handleCandidateRefresh();
                    }}
                  />
                ) : null
              }
            >
              <TabsItem
                name="HOME_TAB"
                label="Find Talent"
                onClick={() => {
                  setSearchParams(() => {
                    const existing = WindowUtil.getUrlSearchParams();
                    existing.set('view', 'home');
                    return existing;
                  });
                }}
              >
                <Gutter top="16px">
                  <Visible only="mobile">
                    <Gutter bottom="16px">
                      <Button
                        name="TALENT_SEARCH_SHOW_MOBILE_FILTERS"
                        className="talent-search-filters-show-filters-button"
                        onClick={() => {
                          setShowMobileFilterModal(true);
                        }}
                        pattern="secondary"
                        fluid
                      >
                        Show filters
                      </Button>
                    </Gutter>
                  </Visible>
                  <TalentSearchFindTalent
                    setInquiredCandidate={setInquiredCandidate}
                    searchResponse={searchResponse}
                    displayFilters={displayFilters}
                    user={user}
                    activeFilters={activeFilters}
                    handleReachOutClick={() => {
                      setShowHubspotExternalReachOutModal(true);
                    }}
                    isPricingShown={isPricingShown}
                  />
                </Gutter>
              </TabsItem>
              <TabsItem
                name="FAVORITES_TAB"
                countBadge={savedCandidates.length}
                label={<span>Saved</span>}
                onClick={() => {
                  setSearchParams(() => {
                    const existing = WindowUtil.getUrlSearchParams();
                    existing.set('view', 'saved');
                    return existing;
                  });
                }}
              >
                <Gutter top="16px">
                  <Suspense>
                    <TalentSearchFavorited
                      activeFilters={activeFilters}
                      user={user}
                      setInquiredCandidate={setInquiredCandidate}
                      isPricingShown={isPricingShown}
                    />
                  </Suspense>
                </Gutter>
              </TabsItem>
            </Tabs>
          </GridColumn>
        </GridRow>
      </Grid>
      {showHubspotExternalReachOutModal ? (
        <ExternalSearchReachOutModal
          onClose={() => {
            setShowHubspotExternalReachOutModal(false);
          }}
          activeFilters={activeFilters}
          user={user}
        />
      ) : null}
      {inquiredCandidate ? (
        <ContactAxiomModal
          candidate={inquiredCandidate}
          onClose={() => {
            setInquiredCandidate(null);
          }}
        />
      ) : null}
      {showMobileFilterModal ? (
        <TalentSearchFilterSidedrawer
          searchResponse={searchResponse}
          disabled={!isHomeTabSelected}
          displayFilters={displayFilters}
          onClose={() => {
            setShowMobileFilterModal(false);
          }}
        />
      ) : null}
    </Suspense>
  );
};
